import { useContext, useEffect, useState } from "react";
import { find } from "lodash";
import { useTranslation } from "react-i18next";
import { ColorGroupThemePickerControlled } from "./ColorGroupThemePickerControlled";
import { ConnectForm } from "@/utils/formHelper";
import { ThemePickerProps } from "./types";
import { QREditorContextProps } from "@/contexts/QREditorContext/types";
import { QREditorContext } from "@/contexts/QREditorContext";
import { VALIDATION_RULES } from "./validations";
import { DEFAULT_CUSTOM_THEME_COLOR } from "./constants";
import { Button as ButtonLib } from "@npm_leadtech/qr-ui-components";
import { MdOutlineAdd } from "react-icons/md";
import { useWindowSize } from "@/hooks/browser/useWindowsSize";
import { useFormContext } from "react-hook-form";
import { FormValues } from "./ColorGroupThemePickerControlled/types";
import "./styles.scss";

export const themePickerInitialColors = [
  { key: "blue", primary: "#1466b8", secondary: "#d1e5fa" },
  { key: "green", primary: "#0e8b70", secondary: "#e8fce8" },
  { key: "yellow", primary: "#998600", secondary: "#fff9cc" },
  { key: "red", primary: "#b00223", secondary: "#fecdd6" },
];

export let themePickerCustomColors = [];

export function ThemePicker({ dataQA, labelPrimary, labelSecondary, onClick }: ThemePickerProps) {
  const { t } = useTranslation();
  const { qrData } = useContext<QREditorContextProps<any>>(QREditorContext);
  const [colors] = useState(themePickerInitialColors);
  const methods = useFormContext<FormValues>();
  const screenSize = useWindowSize();
  const isMobile = screenSize.isMobile;

  const { clearErrors } = methods;

  const currentThemeColor = qrData?.data?.themeColor?.buttonColor;

  const COLOR_GROUP_PROPS = {
    component: "ColorGroupControlled",
    className: "u-flex",
    name: "themeColor",
    primaryColor: {
      label: "Button",
      color: qrData?.data?.themeColor?.buttonColor || DEFAULT_CUSTOM_THEME_COLOR.primary,
      className: "u-spacing-5",
      id: "buttonColor",
      name: "buttonColor",
      errorMessage: [
        { rule: "required", message: "required_message" },
        { rule: "pattern", message: "invalid_color" },
      ],
    },
    secondaryColor: {
      label: "Background",
      color: qrData?.data?.themeColor?.backgroundColor || DEFAULT_CUSTOM_THEME_COLOR.secondary,
      className: "u-spacing-5",
      id: "backgroundColor",
      name: "backgroundColor",
      errorMessage: [
        { rule: "required", message: "required_message" },
        { rule: "pattern", message: "invalid_color" },
      ],
    },
  };

  const isDefaultColorSelected = !!find(themePickerInitialColors, {
    primary: qrData.data.themeColor.buttonColor,
  });

  const [customColors, setCustomColors] = useState({
    key: DEFAULT_CUSTOM_THEME_COLOR.key,
    hidden: isDefaultColorSelected,
    primary: qrData?.data?.themeColor?.buttonColor || DEFAULT_CUSTOM_THEME_COLOR.primary,
    secondary: qrData?.data?.themeColor?.backgroundColor || DEFAULT_CUSTOM_THEME_COLOR.secondary,
  });

  const initialColorSelectedKey =
    themePickerInitialColors.find((theme) => {
      return theme.primary === currentThemeColor;
    })?.key || themePickerInitialColors[0].key;

  const handleThemeColorClick = (key: string) => {
    if (key !== DEFAULT_CUSTOM_THEME_COLOR.key) {
      setSelectedColor(key);

      setCustomColors((prev) => ({ ...prev, hidden: true }));
    }
  };

  useEffect(() => {
    if (customColors.hidden) {
      onClick && onClick(selectedColor);
    }
  }, [customColors]);

  const handleCustomColorClick = () => {
    if (!customColors.hidden) return;

    setSelectedColor(customColors.key);
    onClick?.(customColors.key);
  };

  const [selectedColor, setSelectedColor] = useState(
    !isDefaultColorSelected ? DEFAULT_CUSTOM_THEME_COLOR.key : initialColorSelectedKey
  );

  useEffect(() => {
    const selectedCustomColors = { ...customColors };
    delete selectedCustomColors.hidden;

    if (!customColors.hidden) {
      themePickerCustomColors = [selectedCustomColors];
      onClick(customColors.key);
    }
  }, [customColors]);

  const handleAddCustomColorClick = () => {
    clearErrors("themeColor.buttonColor");
    clearErrors("themeColor.backgroundColor");
    setCustomColors({ ...DEFAULT_CUSTOM_THEME_COLOR, hidden: false });
    setSelectedColor(DEFAULT_CUSTOM_THEME_COLOR.key);
  };

  return (
    <ConnectForm>
      <div
        data-testid="ThemePicker"
        data-qa={dataQA}
        className="ThemePicker"
      >
        <div className="ThemePicker__wrapper">
          <div className="ThemePicker__item ThemePicker__labels">
            <span className="ThemePicker__label ThemePicker__label--first">{labelPrimary}</span>
            <span className="ThemePicker__label">{labelSecondary}</span>
          </div>

          {colors.map((item) => (
            <div
              className={`ThemePicker__item ${
                selectedColor === item.key ? "ThemePicker--active" : ""
              }`}
              key={item.key}
              data-testid={`theme-picker-color-${item.key}-button`}
              data-qa={`theme-picker-color-${item.key}-button`}
              onClick={() => handleThemeColorClick(item.key)}
            >
              <span
                className="ThemePicker__swatch ThemePicker__swatch--first"
                style={{ backgroundColor: item.secondary }}
              ></span>
              <span
                className="ThemePicker__swatch"
                style={{ backgroundColor: item.primary }}
              ></span>
            </div>
          ))}

          {!customColors.hidden ? (
            <div
              className={`ThemePicker__item ${
                selectedColor === customColors.key ? "ThemePicker--active" : ""
              }`}
              data-testid="theme-picker-color-custom-button"
              data-qa="theme-picker-color-custom-button"
              onClick={handleCustomColorClick}
            >
              <span
                className="ThemePicker__swatch ThemePicker__swatch--first"
                style={{
                  backgroundColor:
                    qrData?.data?.themeColor?.backgroundColor || customColors.secondary,
                }}
              ></span>
              <span
                className="ThemePicker__swatch"
                style={{
                  backgroundColor: qrData?.data?.themeColor?.buttonColor || customColors.primary,
                }}
              ></span>
            </div>
          ) : (
            <div className="ThemePicker__item ThemePicker__item--center">
              <span
                className="ThemePicker__add-button"
                data-testid="theme-picker-color-add-button"
              >
                <ButtonLib
                  size={isMobile ? "small" : "large"}
                  id="theme-picker-color-custom-button"
                  dataQA="theme-picker-color-add-button"
                  // FIXME: There's a problem with the type
                  // dataTestId="theme-picker-color-add-button"
                  iconLeft={<MdOutlineAdd />}
                  ariaLabel="Add custom color"
                  onClick={handleAddCustomColorClick}
                />
              </span>
            </div>
          )}
        </div>

        {selectedColor === customColors.key ? (
          <div
            className="u-box u-box--small u-flex u-flex--wrap u-grey-bg u-spacing-5-top u-flex--space-evenly ThemePicker__custom"
            data-testid="color-group-theme-picker-container"
          >
            <ColorGroupThemePickerControlled
              {...COLOR_GROUP_PROPS}
              secondaryColor={{
                ...COLOR_GROUP_PROPS.secondaryColor,
                color: customColors.secondary || DEFAULT_CUSTOM_THEME_COLOR.secondary,
              }}
              primaryColor={{
                ...COLOR_GROUP_PROPS.primaryColor,
                color: customColors.primary || DEFAULT_CUSTOM_THEME_COLOR.primary,
              }}
              rules={VALIDATION_RULES(t)}
            />
          </div>
        ) : null}
      </div>
    </ConnectForm>
  );
}
